import './QueueDetails.scss';
import { prefixClass } from 'lib/utils';

import { useQueueContext } from '../QueueContext';
import TransactionDetails from '../TransactionDetails/TransactionDetails';
import CMComposer from '../CMComposer/CMComposer';
import Chat from '../Chat/Chat';
import { MessageList } from '../../KSQ2/Panels/ConversationPanel';
import TransactionHeader from '../TransactionHeader/TransactionHeader';
import { QueueMarkAsSolvedButton } from '../../Chat/MarkAsSolvedButton';
import { QueueReCategorizeButton } from '../../Chat/RecategorizeButton';
import QueueTogglePriorityButton from './QueueTogglePriorityButton';
import { useAuthContext } from 'context/useAuthContext';
import { HoneAbilityActions, HoneAbilitySubjects } from '@hone-automation/common';
import classNames from 'classnames';
import { IconQuestion } from 'components/Icons';

// Augment AskRequest type to include priority field
import { AskRequestCommentVisibilities, AskRequest as BaseAskRequest } from '@hone-automation/common/lib/AskRequests';

interface EnhancedAskRequest extends BaseAskRequest {
  priority: 'Blocker' | 'Normal';
}
import { Can } from '@casl/react';
import { CMComposerContexts } from '../CMComposer/types';
import QueueManualRequestButton from './QueueManualRequestButton';
import { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

const QueueDetails = () => {
  const { isBookkeeperAdmin, currentLocationAbility } = useAuthContext();
  const prefix = prefixClass('queue-details-panel');
  const { state } = useQueueContext();
  const { selectedItem, filters } = state;
  const canManageQueue = currentLocationAbility.can(HoneAbilityActions.manage, HoneAbilitySubjects.Queue);

  // SWAP FLAG: Set to true to test new MessageList component, false to use old Chat component
  const { enableQueueNewChat } = useFlags();
  const USE_NEW_MESSAGE_LIST = enableQueueNewChat;

  if (!selectedItem) return null;

  // Cast selectedItem to EnhancedAskRequest to access the priority property
  const enhancedSelectedItem = selectedItem as EnhancedAskRequest;
  const { transaction } = enhancedSelectedItem;

  const manualRequestLabel = useMemo(() => {
    if (selectedItem.status === 'In Progress') {
      return 'for me';
    }
    return canManageQueue ? 'for client' : 'for ks';
  }, [selectedItem.status, canManageQueue]);

  const isInternal =
    selectedItem.comments &&
    selectedItem.comments.length > 0 &&
    selectedItem.comments.every(comment => comment.visibility === AskRequestCommentVisibilities.Internal);

  return (
    <div className={prefix()}>
      <div className={prefix('header')}>
        <TransactionHeader request={selectedItem} />
      </div>
      <div className={classNames(prefix('info'), { 'not-visible': !selectedItem })}>
        {selectedItem && (
          <TransactionDetails request={selectedItem}>
            <div className={prefix('options')}>
              {filters.status !== 'Closed' && !isInternal && (
                <QueueManualRequestButton id={selectedItem.id} label={manualRequestLabel} />
              )}
              {isBookkeeperAdmin &&
                transaction?.id &&
                selectedItem.status !== 'Closed' &&
                selectedItem?.canRecategorize && (
                  <QueueReCategorizeButton
                    txId={selectedItem?.id}
                    locationId={selectedItem.locationId}
                    activeTab={selectedItem.status}
                  />
                )}
              {selectedItem.status !== 'Closed' && (
                <QueueMarkAsSolvedButton
                  activeTab={selectedItem.status}
                  locationId={selectedItem.locationId}
                  txId={selectedItem?.id}
                  isSideView={false}
                />
              )}
              <Can I={HoneAbilityActions.manage} a={HoneAbilitySubjects.Queue} ability={currentLocationAbility}>
                <>
                  <QueueTogglePriorityButton
                    id={selectedItem.id}
                    currentPriority={enhancedSelectedItem.priority}
                    unflagOnly={selectedItem.status === 'Closed'}
                  />
                </>
              </Can>
            </div>
          </TransactionDetails>
        )}
      </div>

      <div className={prefix('messages')}>
        {selectedItem?.comments &&
          (USE_NEW_MESSAGE_LIST ? (
            <MessageList request={selectedItem} comments={selectedItem.comments} />
          ) : (
            <Chat comments={selectedItem.comments} />
          ))}
      </div>
      <div className={prefix('composer-area')}>
        <div className={prefix('composer-area-container')}>
          {selectedItem.status === 'Closed' && !selectedItem.canReopen ? (
            <div className={prefix('cannot-reopen-message')}>
              <IconQuestion />
              <span>This request cannot be reopened. Please check your current reports to flag new issues.</span>
            </div>
          ) : (
            <>
              <CMComposer request={selectedItem} context={CMComposerContexts.queue} />
              <div className={prefix('composer-area-container-warning')}>
                <b>Kitchensync AI</b> can make mistakes. Please double-check responses.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default QueueDetails;
